import React from 'react';
import PropTypes from 'prop-types';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import { Div, Icon, Text } from '@pinpoint/design-kit';

import Button from 'src/components/button';
import CONSTANTS from 'src/store/constants';
import { prettyBytes } from 'src/functions';
import ProgressBar from './progress_bar';

/* eslint-disable react/prop-types */

function FileView({ name, size, onRemove, progress, uploadFileStatus, locked }) {
  return (
    <Div d="flex" flexDir="column" minW="100%" m={{ y: '1rem' }}>
      <Div h="6rem" bg="info300" d="flex" align="center" justify="center" pos="relative">
        <Div d="flex" align="center" justify="center" flexDir="column">
          <Icon name="Attachment" />
          <Text textSize="caption">{`${name} ${prettyBytes(size)}`}</Text>
        </Div>
        {!locked ? (
          <Button
            top="-1rem"
            right="-1rem"
            pos="absolute"
            bg="info500"
            hoverBg="info700"
            rounded="circle"
            shadow="4"
            opacity="0.5"
            w="2rem"
            h="2rem"
            p="0rem"
            disabled={uploadFileStatus === CONSTANTS.API.STARTED}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onRemove();
            }}
          >
            <Icon name="Cross" color="white" />
          </Button>
        ) : null}
      </Div>
      <Div pos="relative" h="0.25rem" m={{ t: '0.5rem' }}>
        <ProgressBar width={progress} />
      </Div>
    </Div>
  );
}

/* eslint-enable react/prop-types */

function DropBox(props) {
  const { onDrop, onSelect, droppedFiles, setDroppedFiles, progress, boxStyle, uploadFileStatus, locked } = props;
  const fileInputRef = React.useRef(/** @type {HTMLInputElement | null} */ (null));
  const [{ canDrop, isOver }, drop] = useDrop({
    accept: [NativeTypes.FILE],
    canDrop() {
      return !locked;
    },
    drop(item, monitor) {
      if (onDrop && !locked) {
        onDrop(props, monitor);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  const isActive = canDrop && isOver;
  return (
    <div ref={drop}>
      <Div
        p="2rem"
        bg="secondary"
        borderColor="tabDisabled"
        border="1px solid"
        d="flex"
        flexDir="column"
        justify="flex-start"
        align="center"
        h="186px"
        overflow="auto"
        rounded="md"
        {...boxStyle}
      >
        <Div w="100%">
          {locked ? (
            <Text textWeight="500" textSize="body">
              File selected from SFTP
            </Text>
          ) : isActive ? (
            <Text textWeight="500" textSize="body">
              Release to drop
            </Text>
          ) : (
            <Div w="100%" d="flex" flexDir="column" align="center" m={{ y: '-0.5rem' }}>
              <Div pos="relative" w="100%" d="flex" flexDir="column" align="center" m={{ b: '6px' }}>
                <Icon viewBox="0 0 24 24" size="80px" name="Upload" color="white" />
                <Text
                  pos="absolute"
                  textColor="gray800"
                  textSize="body"
                  textWeight="500"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                >
                  Drag file to upload
                </Text>
              </Div>
              <Button
                buttonStyle="primary"
                m={{ r: '0rem' }}
                onClick={() => {
                  const fileInput = fileInputRef.current;
                  if (fileInput) {
                    fileInput.value = '';
                    fileInput.click();
                  }
                }}
              >
                SELECT
              </Button>
              <input
                style={{ opacity: '0', width: '0', height: '0' }}
                type="file"
                name="file"
                onChange={(event) => {
                  const { target } = event;
                  onSelect(target.files);
                  target.value = '';
                }}
                ref={fileInputRef}
              />
            </Div>
          )}
        </Div>
        <Div d="flex" justify="center" w="100%" flexDir="column">
          {droppedFiles.map(({ name, size }, index) => (
            <FileView
              key={name}
              name={name}
              size={size}
              progress={progress[index]}
              onRemove={() => {
                setDroppedFiles(droppedFiles.filter((file, fileIndex) => fileIndex !== index));
              }}
              uploadFileStatus={uploadFileStatus}
              locked={locked}
            />
          ))}
        </Div>
      </Div>
    </div>
  );
}

DropBox.propTypes = {
  onDrop: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  droppedFiles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  progress: PropTypes.arrayOf(PropTypes.number).isRequired,
  setDroppedFiles: PropTypes.func.isRequired,
  boxStyle: PropTypes.shape({}),
  uploadFileStatus: PropTypes.string,
  locked: PropTypes.bool,
};

DropBox.defaultProps = {
  boxStyle: {},
  uploadFileStatus: '',
  locked: false,
};

export default DropBox;
